import { useQueryClient } from "@tanstack/react-query";
import React from "react";

import { isUserRole } from "@examspark/common";
import {
  ActivityEditorConfig,
  ActivityEditorModal,
  useModal,
  useAppContext,
} from "@examspark/common-app";
import { VFlex } from "@examspark/common-ui";
import Button from "@examspark/common-ui/dist/components/buttons/Button";
import { IconAddLine } from "@examspark/common-ui/dist/components/icons";
import { ResourceVisibilityType, WorkspaceUserRole } from "@examspark/types";

import Browse, { FilterData } from "../browse/Browse";
import BrowseList from "../browse/BrowseList";

const defaultSearchFilters: FilterData = {
  type: ["DISTRICT_ASSESSMENT"],
};

export const activityEditorConfig: ActivityEditorConfig = {
  allowItemCreation: false,
  allowItemCustomization: false,
  enablePassagePicker: false,
  fields: {
    subject: {
      show: true,
      required: true,
    },
  },
  activitySettings: {
    show: true,
    fields: {
      title: { show: true },
      subject: { show: true },
    },
  },
  itemPicker: {
    filters: {
      bank: { show: true },
      language: { show: true },
      subject: { show: true },
      grade: { show: true },
      unit: { show: true },
      course: { show: true },
      objective: { show: true },
      interaction: { show: false },
      customTags: [
        {
          key: "question-format",
          label: "Question Type",
        },
      ],
    },
  },
  visibility: ResourceVisibilityType.Private,
};

export const WorkspaceAssessments = () => {
  const [searchFilters, setSearchFilters] =
    React.useState<FilterData>(defaultSearchFilters);
  const newActivityModal = useModal();
  const client = useQueryClient();

  const [context] = useAppContext();
  const canAddAssessments = isUserRole(
    context.workspaceUser,
    WorkspaceUserRole.ContentManager,
  );

  return (
    <VFlex>
      <ActivityEditorModal
        {...newActivityModal.modalProps}
        onSave={() => {
          client.invalidateQueries(["browse"]);
          newActivityModal.close();
        }}
        editorConfig={activityEditorConfig}
      />
      <Browse
        resultList={BrowseList}
        withFilters={{
          unitByTag: true,
          courseByTag: true,
          subject: true,
        }}
        searchFilter={searchFilters}
        setSearchFilter={setSearchFilters}
        onFilterClear={() => setSearchFilters(defaultSearchFilters)}
        pageTitle="Browse Assessments"
        toolbar={
          <>
            {canAddAssessments && (
              <Button
                title="Create Assessment"
                onClick={() => newActivityModal.open()}
                icon={<IconAddLine />}
                color="create"
              >
                Assessment
              </Button>
            )}
          </>
        }
      />
    </VFlex>
  );
};
